import React from "react";
import { Skeleton } from "antd";

import "../../tokens/tokens.css";
import "./index.css";

/**
 * V2Table — a generic frozen-column, grouped-header, sectioned-row table.
 *
 * Deliberately independent of antd's Table: this shape (sticky leading
 * columns, two-line grouped column headers, section-banner rows spliced
 * between data rows, a permanently pinned footer row) doesn't map onto
 * antd's row/column/pagination model without fighting it. This component
 * only knows about layout/visual state (loading, empty, sticky regions) —
 * all cell content and business meaning is supplied by the caller via
 * `renderHeader`/`renderCell`/`renderSection`/`renderCell` on the column
 * and footer definitions.
 *
 * Props:
 * - frozenColumns: [{ key, width, renderHeader(), renderCell(row) }]
 *     Leading columns pinned to the left while the group columns scroll.
 * - groupColumns: [{ key, width, renderHeader(), renderCell(row) }]
 *     The horizontally-scrollable columns (e.g. one per store).
 * - rows: [{ key, type: 'data' | 'section', renderSection?() }]
 *     'section' rows render as a full-width banner (renderSection()).
 *     'data' rows are passed as-is to each column's renderCell(row).
 * - footer?: { label, renderCell(groupColumn) } — pinned aggregate row.
 * - loading?: boolean — shows skeleton rows, like the antd tables elsewhere
 *     in the admin panel. Store columns aren't known until data arrives, so
 *     `skeletonColumnCount` placeholder columns stand in for them.
 * - emptyMessage?: string
 * - maxHeight?: number — clips + scrolls the table body (header/footer stay pinned).
 */
export default function V2Table({
  frozenColumns = [],
  groupColumns = [],
  rows = [],
  footer,
  loading = false,
  skeletonColumnCount = 6,
  emptyMessage = "No data to display.",
  maxHeight = 659,
}) {
  let frozenLeft = 0;
  const frozenOffsets = frozenColumns.map((col) => {
    const left = frozenLeft;
    frozenLeft += col.width;
    return left;
  });
  const totalFrozenWidth = frozenLeft;

  if (loading) {
    const skeletonWidth = groupColumns[0]?.width ?? 110;
    const skeletonColumns = [...Array(skeletonColumnCount)].map((_, i) => `skeleton-${i}`);
    const renderSkeletonRow = (className, rowKey) => (
      <div key={rowKey} className={`v2-table-row ${className}`}>
        {frozenColumns.map((col, i) => (
          <div
            key={col.key}
            className="v2-table-cell v2-table-cell-frozen v2-table-skeleton-cell"
            style={{ width: col.width, left: frozenOffsets[i] }}
          >
            <Skeleton round active paragraph={false} />
          </div>
        ))}
        {skeletonColumns.map((key) => (
          <div key={key} className="v2-table-cell v2-table-skeleton-cell" style={{ width: skeletonWidth }}>
            <Skeleton round active paragraph={false} />
          </div>
        ))}
      </div>
    );

    return (
      <div className="v2-table" style={{ maxHeight }} aria-busy="true">
        {renderSkeletonRow("v2-table-header-row", "header")}
        {[...Array(6)].map((_, i) => renderSkeletonRow("v2-table-data-row", `row-${i}`))}
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="v2-table v2-table-placeholder" style={{ maxHeight }}>
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="v2-table" style={{ maxHeight }}>
      <div className="v2-table-row v2-table-header-row">
        {frozenColumns.map((col, i) => (
          <div
            key={col.key}
            className="v2-table-cell v2-table-cell-frozen"
            style={{ width: col.width, left: frozenOffsets[i] }}
          >
            {col.renderHeader()}
          </div>
        ))}
        {groupColumns.map((col) => (
          <div key={col.key} className="v2-table-cell" style={{ width: col.width }}>
            {col.renderHeader()}
          </div>
        ))}
      </div>

      {rows.map((row) => {
        if (row.type === "section") {
          return (
            <div key={row.key} className="v2-table-row v2-table-section-row">
              <div className="v2-table-section-label">{row.renderSection()}</div>
            </div>
          );
        }

        return (
          <div key={row.key} className="v2-table-row v2-table-data-row">
            {frozenColumns.map((col, i) => (
              <div
                key={col.key}
                className="v2-table-cell v2-table-cell-frozen"
                style={{ width: col.width, left: frozenOffsets[i] }}
              >
                {col.renderCell(row)}
              </div>
            ))}
            {groupColumns.map((col) => (
              <div key={col.key} className="v2-table-cell" style={{ width: col.width }}>
                {col.renderCell(row)}
              </div>
            ))}
          </div>
        );
      })}

      {footer && (
        <div className="v2-table-row v2-table-footer-row">
          <div
            className="v2-table-cell v2-table-cell-frozen v2-table-footer-label"
            style={{ width: totalFrozenWidth, left: 0 }}
          >
            {footer.label}
          </div>
          {groupColumns.map((col) => (
            <div key={col.key} className="v2-table-cell" style={{ width: col.width }}>
              {footer.renderCell(col)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
